/**
 * BuddyGroupForm — edit form for renaming, primary cardholder, and member management.
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/contexts/ToastContext';
import {
  useUpdateBuddyGroup,
  useAddBuddyGroupMember,
  useRemoveBuddyGroupMember,
} from '../api';
import { PrimaryCardholderSelect } from './PrimaryCardholderSelect';
import { MemberSelector } from './MemberSelector';
import type { BuddyGroupDetail, UpdateBuddyGroupPayload } from '../types/buddy-group';

interface BuddyGroupFormProps {
  group: BuddyGroupDetail;
  onSaved: () => void;
  onCancel: () => void;
}

export function BuddyGroupForm({ group, onSaved, onCancel }: BuddyGroupFormProps) {
  const { showSuccess, showError } = useToast();
  const [groupName, setGroupName] = useState(group.group_name || '');
  const [primaryId, setPrimaryId] = useState<string | null>(group.primary_member_user_profile_id);

  const updateMutation = useUpdateBuddyGroup();
  const addMember = useAddBuddyGroupMember();
  const removeMember = useRemoveBuddyGroupMember();

  const handleSave = async () => {
    try {
      const payload: UpdateBuddyGroupPayload = {};
      if (groupName !== (group.group_name || '')) payload.group_name = groupName;
      if (primaryId !== group.primary_member_user_profile_id) payload.primary_member_user_profile_id = primaryId;

      if (Object.keys(payload).length > 0) {
        await updateMutation.mutateAsync({ id: group.id, payload });
      }
      showSuccess('Group updated');
      onSaved();
    } catch (err: any) {
      showError('Update failed', err?.message ?? 'An error occurred.');
    }
  };

  const handleAddMember = async (userProfileId: string) => {
    try {
      await addMember.mutateAsync({ groupId: group.id, payload: { user_profile_id: userProfileId } });
      showSuccess('Member added');
    } catch (err: any) {
      showError('Failed to add member', err?.message ?? 'An error occurred.');
    }
  };

  const handleRemoveMember = async (memberProfileId: string) => {
    try {
      await removeMember.mutateAsync({ groupId: group.id, memberProfileId });
      // If the removed member was the selected primary, clear the stale reference
      if (primaryId === memberProfileId) {
        setPrimaryId(null);
      }
      showSuccess('Member removed');
    } catch (err: any) {
      showError('Failed to remove member', err?.message ?? 'An error occurred.');
    }
  };

  const isSaving = updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Group name */}
      <div className="space-y-1">
        <label className="text-sm font-medium text-foreground" htmlFor="group-name">
          Group Name
        </label>
        <Input
          id="group-name"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          placeholder="Enter group name..."
        />
      </div>

      {/* Primary cardholder */}
      <PrimaryCardholderSelect
        members={group.members}
        value={primaryId}
        onChange={setPrimaryId}
      />

      {/* Current members */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-foreground">Current Members ({group.members.length})</h3>
        <ul className="space-y-1">
          {group.members.map((m) => (
            <li key={m.id} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
              <div className="flex items-center gap-2">
                <span>{m.display_name}</span>
                {m.is_primary && <Badge variant="secondary" className="text-xs">Primary</Badge>}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveMember(m.user_profile_id)}
                disabled={removeMember.isPending}
                title="Remove member"
              >
                <X className="h-4 w-4 text-destructive" />
              </Button>
            </li>
          ))}
        </ul>
      </div>

      {/* Add member */}
      <MemberSelector onAdd={handleAddMember} isLoading={addMember.isPending} />

      {/* Save / Cancel */}
      <div className="flex items-center gap-2 pt-2">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
